/**
 * The door as the backoffice reads it: what a booking holds, in words, and which states the
 * sponsor still works in. The door's form and its writes are in `@countdown/door-editor`.
 */

import type { components } from "@countdown/api-client";
import { FIELD_NAMES, fileLine, isVideo } from "@countdown/door-editor";

import type { Clock } from "@/lib/clock";

export {
  FIELD_NAMES,
  fileLine,
  isVideo,
  pictureLine,
  type DoorDocument,
  type DoorWrites,
} from "@countdown/door-editor";

type Booking = components["schemas"]["EditorialBookingView"];
type BookingState = components["schemas"]["BookingWorkflowState"];
type ButtonView = components["schemas"]["ButtonView"];
type SlideMedia = components["schemas"]["SlideMediaView"];
type ActionStatus = components["schemas"]["ActionStatus"];

/** An action that has been in front of participants: its doors may have figures. */
const SHOWN_STATUSES: ReadonlySet<ActionStatus> = new Set(["live", "paused", "finished", "archived"]);

/**
 * Whether the door has opened to participants, so its figures can say anything (23.10.2026).
 * A door without a sponsor counts as well: it opens with the fallback and is viewed.
 */
export function hasOpened(
  action: { readonly status: ActionStatus },
  field: { readonly opens_at: string },
  clock: Clock,
): boolean {
  return SHOWN_STATUSES.has(action.status) && Date.parse(field.opens_at) <= clock.now().getTime();
}

/** Released or open: a change the backoffice saves is public at once. */
const PUBLIC_STATES: ReadonlySet<BookingState> = new Set(["approved", "live"]);

export function isPublic(state: BookingState): boolean {
  return PUBLIC_STATES.has(state);
}

/**
 * What a video has for captions (WCAG 1.2.2): the file and its verdict, the sponsor's
 * confirmation that it needs none, or neither, which blocks the submission.
 */
export function captionsLine(media: SlideMedia): string {
  if (media.captions) {
    return fileLine(media.captions);
  }
  return media.needs_no_captions
    ? "keine Datei, bestätigt: keine gesprochene Sprache, oder die Untertitel sind im Bild"
    : "fehlen: weder Datei noch Bestätigung";
}

/** Where a slide's button leads, in words. */
export function buttonLine(button: ButtonView): string {
  if (button.action === "next") {
    return "weiter zur nächsten Folie oder zum Quiz";
  }
  return button.url ? `Link auf ${button.url}` : "Link, die Adresse fehlt";
}

/**
 * What a stored door holds, by the names of the door plan's row (`BackofficeFieldView`): the
 * server counts it there, and {@link modulesOf} counts a booking the same way.
 */
export interface DoorModules {
  readonly slide_count: number;
  readonly video_count: number;
  readonly question_count: number;
  readonly has_prize_image: boolean;
}

export function modulesOf(booking: Booking): DoorModules {
  return {
    slide_count: booking.slides.length,
    video_count: booking.slides.filter((slide) => isVideo(slide.media)).length,
    question_count: booking.quiz.questions.length,
    has_prize_image: booking.prize_image !== null,
  };
}

/** Holds a number to its noun and a separator to the part before it, where a line breaks. */
const NBSP = " ";

/**
 * What the door holds, in one line: "3 Folien · 1 Video · 2 Fragen · Gewinnbild". Counted on
 * the stored door, so it says what a release would publish. One line for the door page and
 * the plan's "Module" column.
 *
 * In the plan's narrow column the line wraps, and only after a separator: "3 Folien ·" over
 * "2 Fragen ·", never "2" at the end of one line and "Fragen" at the start of the next.
 */
export function moduleSummary(door: DoorModules): string {
  const parts = [
    counted(door.slide_count, "Folie", "Folien"),
    counted(door.video_count, "Video", "Videos"),
    counted(door.question_count, "Frage", "Fragen"),
    door.has_prize_image ? "Gewinnbild" : null,
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? "Noch kein Inhalt" : parts.join(`${NBSP}· `);
}

function counted(count: number, one: string, many: string): string | null {
  if (count === 0) {
    return null;
  }
  return `${String(count)}${NBSP}${count === 1 ? one : many}`;
}

/**
 * What a submission is still missing, in words.
 *
 * The keys come from `missing_for_submission` on the server, the same function the
 * sponsor's own form uses to grey out its button. Translated here rather than there,
 * because the API answers field names and this is the only place that shows them to a
 * person. Each key stands once however many slides or questions lack it, so the label says
 * what to look for, not where.
 */
const MISSING_LABELS: Readonly<Record<string, string>> = {
  slides: "Folie mit Titel oder Text",
  slide_button_url: "Adresse eines Link-Knopfs",
  video_poster: "Vorschaubild je Video",
  video_text: "Textfassung je Video",
  video_captions: "Untertiteldatei oder Bestätigung je Video",
  prize_description: FIELD_NAMES.prize_description,
  prize_quantity: FIELD_NAMES.prize_quantity,
  // A save drops a deleted question and puts an archived one back in use: no replacement needed.
  sponsor_question: "Frage prüfen und neu speichern (archiviert oder gelöscht)",
  answer_options: "2 bis 4 Antworten je Frage",
  correct_option_key: "richtige Antwort je Frage",
};

export function missingLabel(key: string): string {
  return MISSING_LABELS[key] ?? key;
}

/** One line of a door's checklist (D40): what a door needs before its day. */
export interface ChecklistItem {
  readonly label: string;
  readonly done: boolean;
  /** What is missing, or the deadline; shown after the label. */
  readonly detail?: string;
}

/** A door's content as the door page and the plan both know it (D94). */
export interface DoorContentFacts {
  readonly slides: number;
  readonly questions: number;
  readonly prizeImage: boolean;
  /** The keys of `missing_for_submission`. */
  readonly missing: readonly string[];
}

const SLIDE_PARTS: ReadonlySet<string> = new Set([
  "slides",
  "slide_button_url",
  "video_poster",
  "video_text",
  "video_captions",
]);
const QUESTION_PARTS: ReadonlySet<string> = new Set([
  "sponsor_question",
  "answer_options",
  "correct_option_key",
]);
const PRIZE_PARTS: ReadonlySet<string> = new Set(["prize_description", "prize_quantity"]);

/** One line per part of the content; `parts` names what the server says is missing there. */
function contentLine(
  label: string,
  present: boolean,
  missing: readonly string[],
  parts: ReadonlySet<string>,
  absent: string,
): ChecklistItem {
  const lacking = missing.filter((key) => parts.has(key)).map(missingLabel);
  const detail = present ? lacking.join(", ") : absent;
  return { label, done: present && lacking.length === 0, ...(detail === "" ? {} : { detail }) };
}

/**
 * The door's content in three lines (D94): its slides, its questions, its prize with picture.
 * The quiz and the picture count although the server lets a door be handed in without them:
 * a door is complete with both, a submission only needs what the sponsor cannot leave out.
 */
export function contentChecklist(door: DoorContentFacts): readonly ChecklistItem[] {
  const words = (count: number, one: string, many: string): string =>
    `${String(count)} ${count === 1 ? one : many} gepflegt`;
  return [
    contentLine(
      words(door.slides, "Folie", "Folien"),
      door.slides > 0,
      door.missing,
      SLIDE_PARTS,
      "noch keine",
    ),
    contentLine(
      words(door.questions, "Frage", "Fragen"),
      door.questions > 0,
      door.missing,
      QUESTION_PARTS,
      "noch keine",
    ),
    contentLine(
      "Gewinn gepflegt",
      door.prizeImage,
      door.missing,
      PRIZE_PARTS,
      "Gewinnbild fehlt",
    ),
  ];
}

/** Done of all, as a whole percent; a list without lines is complete. */
export function progressOf(items: readonly ChecklistItem[]): number {
  if (items.length === 0) {
    return 100;
  }
  return Math.round((100 * items.filter((item) => item.done).length) / items.length);
}

type FieldView = components["schemas"]["BackofficeFieldView"];

/**
 * A booked door's checklist from its row in the plan (D94): the lines of the door page, way and
 * content, so the plan's percent and the page's ticks agree. `null` for an unbooked door.
 */
export function fieldChecklist(field: FieldView): readonly ChecklistItem[] | null {
  const state = field.booking_state;
  if (state === null) {
    return null;
  }
  const designs = field.maintenance === "publisher_designs";
  return [
    ...(field.maintenance === "publisher_only"
      ? []
      : [{ label: "Ansprechpartner hinterlegt", done: field.contact_configured === true }]),
    ...(field.invites || designs
      ? [
          {
            label: "Einladung versendet",
            done: field.invitation_sent || (designs && field.portal_account),
          },
        ]
      : []),
    { label: "Logo im Kalender", done: field.logo_published === true },
    { label: "Freigegeben", done: isPublic(state) || state === "finished" },
    ...contentChecklist({
      slides: field.slide_count,
      questions: field.question_count,
      prizeImage: field.has_prize_image,
      missing: field.missing ?? [],
    }),
  ];
}

/** One open task of the plan (D94): what to do, and where it is done. */
export interface OpenTask {
  readonly key: string;
  readonly text: string;
  readonly to: string;
}

/**
 * The plan's open tasks (D94), derived from its rows: none is stored, so a task is gone the
 * moment its row says it is done. In door order; a firm's task (contact, logo, invitation)
 * stands once, at its first door. An unbooked door is no task: sponsors come until the go-live.
 * The invitation is one only while the action runs (`live`): before, it goes out by itself.
 */
export function openTasks(
  fields: readonly FieldView[],
  firm: (sponsorId: string) => string,
  live: boolean,
): readonly OpenTask[] {
  const tasks: OpenTask[] = [];
  const named = new Set<string>();
  const once = (key: string, task: () => OpenTask): void => {
    if (!named.has(key)) {
      named.add(key);
      tasks.push(task());
    }
  };
  for (const field of fields) {
    const sponsorId = field.sponsor_id ?? null;
    const state = field.booking_state;
    if (sponsorId === null || state === null) {
      continue;
    }
    const company = field.sponsor_company ?? "";
    const door = `tuerchen/${String(field.position)}`;
    if (state === "submitted" || state === "in_review") {
      tasks.push({
        key: `pruefen-${door}`,
        text: `Türchen ${String(field.position)} prüfen`,
        to: `${door}?bearbeiten`,
      });
    }
    if (field.sponsor_approval === "changes_requested") {
      tasks.push({
        key: `wunsch-${door}`,
        text: `Änderungswunsch zu Türchen ${String(field.position)}`,
        to: `${door}?bearbeiten`,
      });
    }
    if (field.deadline_overdue) {
      tasks.push({
        key: `frist-${door}`,
        text: `Freigabefrist von Türchen ${String(field.position)} abgelaufen`,
        to: `${door}?bearbeiten`,
      });
    }
    if (field.logo_pending === true) {
      once(`logo-${sponsorId}`, () => ({
        key: `logo-${sponsorId}`,
        text: `Logo von ${company} prüfen`,
        to: firm(sponsorId),
      }));
    } else if (field.logo_published !== true && (isPublic(state) || state === "finished")) {
      once(`logo-${sponsorId}`, () => ({
        key: `logo-${sponsorId}`,
        text: `Logo von ${company} fehlt im Kalender`,
        to: firm(sponsorId),
      }));
    }
    if (field.maintenance !== "publisher_only" && field.contact_configured !== true) {
      once(`kontakt-${sponsorId}`, () => ({
        key: `kontakt-${sponsorId}`,
        text: `Kontakt-E-Mail von ${company} fehlt`,
        to: firm(sponsorId),
      }));
    } else if (live && field.invites && !field.invitation_sent && !field.portal_account) {
      once(`einladen-${sponsorId}`, () => ({
        key: `einladen-${sponsorId}`,
        text: `${company} einladen`,
        to: door,
      }));
    }
  }
  return tasks;
}
